// @flow
import React, { Component, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames/bind';
import { Base64 } from "js-base64";
import { Helmet } from "react-helmet";
import { Mobile } from "../../../../utils/responsive";
import { Link } from "react-router-dom";
import { CommaFormat } from '../../../../elements/Price/Price';

import style from './ProductBase.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import icons from '../../../../assets/styles/icons.scss';

import type { OnlineStore } from '../../../../entities';
import type { Node } from 'react';

type Props = {
  +name: string;
  +image?: string;
  +price: string;
  +shipping?: string;
  +store: { [key: string]: string };
  +url: string;
  +information?: { [key: string]: string }[];
  +capacity?: string;
  +additionalDetail?: string;
  +color?: string;
  +onlineStore: OnlineStore;
  +categoryId: number;
  +volumeTier?: string;
  +children?: Node;
  +preOrderInfo?: Node;
  +preferredImportationMethod?: string;
  +weight?: string;
  +comingSoon?: boolean;
};

type State = {
  scrollYPosition: number;
};

export default class ProductBase extends Component<Props, State> {
  featuresWrapRef = createRef();
  rightWrapRef = createRef();
  state = { scrollYPosition: 0 };


  componentDidMount() {
    window.addEventListener('scroll', this.handleScrollListener)
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScrollListener);
  }

  handleScrollListener = () => {
    this.setState({ scrollYPosition: window.scrollY });

    window.requestAnimationFrame(() => {
      if (this.rightWrapRef && this.rightWrapRef.current &&
        this.featuresWrapRef && this.featuresWrapRef.current) {
        const rwRef = this.rightWrapRef.current;
        const qswRef = this.featuresWrapRef.current;
        const qswStyle = window.getComputedStyle(qswRef);
        const qswHeight = parseFloat(qswStyle.getPropertyValue('height'));
        const qswPaddingTop = parseFloat(qswStyle.getPropertyValue('padding-top'));
        const qswPaddingBottom = parseFloat(qswStyle.getPropertyValue('padding-bottom'));
        const qswActualHeight = qswHeight - qswPaddingTop - qswPaddingBottom;

        if ((qswActualHeight - rwRef.offsetHeight) > this.state.scrollYPosition) {
          rwRef.style.top = `${Math.max(0, this.state.scrollYPosition)}px`;
        }
      }
    });
  };

  encodeProduct = () => {
    const { name, url, price, image, shipping, capacity, additionalDetail, color, onlineStore, categoryId, volumeTier, weight } = this.props;
    let cap = '';
    
    if (capacity) {
      cap = `${capacity} GB`;
      if (capacity === '1024') {
        cap = '1 TB';
      }else if (capacity === '2048') {
        cap = '2 TB';
      }
    }
    
    let detail = '';
    
    if (color) detail += `Color: ${color && color.replace('-', ' ')}`;
    if (cap) detail += `\nCapacity: ${cap}`;
    if (additionalDetail) detail += `\n${additionalDetail}`;
    
    const a: any = {
      name,
      url,
      // price,
      image,
      detail,
      categoryId,
    };
    
    if (!!weight) a.weight = weight;

    if (!!price) {
      a.price = price.replace(/,/g, '');
    }

    if (!!shipping) {
      a.shipping = shipping.replace(/,/g, '');
    }

    if (!!onlineStore) {
      a.onlineStore = onlineStore;
    }

    if (!!volumeTier) {
      a.volumeTier = volumeTier;
    }

    return Base64.encode(JSON.stringify(a));
  };

  render() {
    const { children, name, url, image, store, price, information, preOrderInfo, preferredImportationMethod, comingSoon } = this.props;
    
    const importationMethod = 
      !!preferredImportationMethod ? `preferred_importation_method=${preferredImportationMethod}&` : '';

    const benefits = (
        <Fragment>
          {preOrderInfo}
          <FormattedMessage id="customProduct.base.benefits.title">{
            text => <p className={style.title}><i className={icons.iconStarMedium}/> {text}: </p>
          }</FormattedMessage>
          <FormattedMessage id="customProduct.base.benefits.content">{
            text => <p>{text} </p>
          }</FormattedMessage>
        </Fragment>
    );
    
    // TODO: Might come back anytime soon
    /*const iphone16Message = name.startsWith('iPhone 16') && (
      <Fragment>
        <p>El iPhone 16 será anunciado muy pronto.</p>
      </Fragment>
    );*/
    /*const iphone16Message = url.includes('iphone-16') && (
        <Fragment>
          {/!*<p className={style.title}><i className={icons.iconStarMedium}/> Disponible por Qempo Lite: </p>*!/}
          <p>{/!*El iPhone 16 solo estará disponible por Qempo Lite. <br/>*!/}
            <a href="https://youtu.be/45M0LEcKcuE" target="_blank">Mira aquí un video tutorial sobre cómo comprar tu iPhone 16 por Qempo Lite</a>.
          </p>
        </Fragment>
    );*/
    const iphone16Message = undefined;

    return (
      <Fragment>
        <Mobile>
          <Helmet key="helmet">
            <style type="text/css">{`
              div#root {
                  padding-bottom: 168px!important;
              }
          `}</style>
          </Helmet>
        </Mobile>
        <section className={style.productBase}>
          <section
            ref={this.featuresWrapRef}
            className={style.features}
          >
            <section className={style.left}>
              <h1 className={style.title}>{name}</h1>
              {image && <img src={image} alt={name}/>}
              <section className={style.children}>
                {children}
              </section>
              <section className={classNames(style.benefits, style.mobile)}>
                {iphone16Message}
                <br/>
                {benefits}
              </section>
            </section>
            <section
              ref={this.rightWrapRef}
              className={classNames(style.right, style.desktop)}
            >
              <section className={style.box}>
                <section className={style.content}>
                  <section className={style.store}>
                    <FormattedMessage id="customProduct.base.soldBy">{
                      text => <p>{text}:</p>
                    }</FormattedMessage>
                    <img src={store.image} alt="Qempo"/>
                  </section>
                  <section className={style.price}>
                    <FormattedMessage id="customProduct.base.price">{
                      text => <p>{`${text} ${store.country}:`}</p>
                    }</FormattedMessage>
                    <p className={style.value}>{price ? `$ ${CommaFormat({ price })}` : '$ --.--'}</p>
                  </section>
                </section>
                <FormattedMessage id="customProduct.base.button">{
                  text => <Link className={classNames(buttonStyle.btn, buttonStyle.lightBlue, {[style.disabled]: comingSoon})}
                                to={`/new-order/product?${importationMethod}product=${this.encodeProduct()}`}>{comingSoon ? 'Próximamente' : text}</Link>
                }</FormattedMessage>
              </section>
              <section className={classNames(style.benefits, style.desktop)}>
                {iphone16Message}
                <br/>
                {benefits}
              </section>
            </section>
          </section>
          {information && <section className={style.information}>
            {information.map((info: { [string]: string }, index: number) => (
              <article key={index}>
                <h2>{info.title}</h2>
                <p>{info.text}</p>
              </article>))}
          </section>}
          <section className={classNames(style.box, style.fixed)}>
            <section className={style.content}>
              <section className={style.store}>
                <FormattedMessage id="customProduct.base.soldBy">{
                  text => <p>{text}:</p>
                }</FormattedMessage>
                <img src={store.image} alt="Qempo"/>
              </section>
              <section className={style.price}>
                <FormattedMessage id="customProduct.base.price">{
                  text => <p>{`${text} ${store.country}:`}</p>
                }</FormattedMessage>
                <p className={style.value}>{price ? `$ ${price}` : '$ --.--'}</p>
              </section>
            </section>
            <FormattedMessage id="customProduct.base.button">{
              text => <Link className={classNames(buttonStyle.btn, buttonStyle.lightBlue, {[style.disabled]: comingSoon})}
                            to={`/new-order/product?${importationMethod}product=${this.encodeProduct()}`}>{comingSoon ? 'Próximamente' : text}</Link>
            }</FormattedMessage>
          </section>
        </section>
      </Fragment>
    )
  }
}

ProductBase.propTypes = {
  name: PropTypes.string.isRequired,
  image: PropTypes.string,
  price: PropTypes.string,
  shipping: PropTypes.string,
  store: PropTypes.object.isRequired,
  url: PropTypes.string.isRequired,
  information: PropTypes.array,
  capacity: PropTypes.string,
  additionalDetail: PropTypes.string,
  color: PropTypes.string,
  onlineStore: PropTypes.object,
  categoryId: PropTypes.number.isRequired,
  volumeTier: PropTypes.string,
  preOrderInfo: PropTypes.node,
  preferredImportationMethod: PropTypes.string,
  weight: PropTypes.string,
  comingSoon: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/modules/custom-products/components/ProductBase/ProductBase.jsx